import { BALANCE } from '../config/balance';
import { PALETTE } from '../assets/palette';
import { clamp, lerp } from '../core/vec';
import type { Engine } from '../game/engine';
import type { Enemy } from '../game/enemies';
import { drawSprite, drawSpriteRotT, sprite } from './sprites';
import {
  arrowBoltSprite, bossBodySprite, bomberSprite, chargerSprite,
  chaserSprite, coinSprite, eliteSprite, foeBoltSprite, gemSprite,
  heartSprite, playerSprite, spitterSprite, staffBoltSprite,
} from './sprite-defs';

type Ctx = CanvasRenderingContext2D;
export interface Bounds { x0: number; y0: number; x1: number; y1: number; }

const inView = (x: number, y: number, b: Bounds): boolean =>
  x >= b.x0 && x <= b.x1 && y >= b.y0 && y <= b.y1;

export function drawGems(ctx: Ctx, engine: Engine, alpha: number, now: number, b: Bounds): void {
  const heart = heartSprite();
  for (const g of engine.gemPool.all()) {
    if (!g.active) continue;
    const x = lerp(g.prevX, g.x, alpha);
    const y = lerp(g.prevY, g.y, alpha);
    if (!inView(x, y, b)) continue;
    if (g.heart) {
      const pulse = 1 + 0.12 * Math.sin(now / 240 + x);
      drawSprite(ctx, heart, x, y, pulse);
    } else {
      const pulse = 1 + 0.13 * Math.sin(now / 260 + x * 0.7 + y * 0.5);
      drawSprite(ctx, gemSprite(g.core), x, y, pulse);
    }
  }
}

export function drawCoins(ctx: Ctx, engine: Engine, alpha: number, now: number, b: Bounds): void {
  const sp = coinSprite();
  for (const c of engine.coinPool.all()) {
    if (!c.active) continue;
    const x = lerp(c.prevX, c.x, alpha);
    const y = lerp(c.prevY, c.y, alpha);
    if (!inView(x, y, b)) continue;
    const pulse = 1 + 0.1 * Math.sin(now / 300 + x * 0.9);
    drawSprite(ctx, sp, x, y, pulse);
  }
}

function chargerAngle(e: Enemy, engine: Engine, x: number, y: number): number {
  let dx = e.dirX;
  let dy = e.dirY;
  if (e.state === 'approach') {
    const d = Math.hypot(engine.player.x - x, engine.player.y) || 1;
    dx = (engine.player.x - x) / d;
    dy = (engine.player.y - y) / d;
  }
  return Math.atan2(dy, dx);
}

function chargerLook(e: Enemy): { color: string; key: string } {
  if (e.state === 'charge') return { color: PALETTE.warn, key: 'charger-ch' };
  if (e.state === 'tired') return { color: PALETTE.tired, key: 'charger-t' };
  if (e.state === 'telegraph') {
    return Math.sin(e.stateT * 40) > 0
      ? { color: PALETTE.warn, key: 'charger-tg1' }
      : { color: PALETTE.terracotta, key: 'charger-tg2' };
  }
  return { color: PALETTE.charger, key: 'charger-ap' };
}

const AFFIX_MARKER: Record<string, string> = {
  hp: '#4C7D9E', dmg: '#C7483B', speed: '#D97757', size: '#C99A2E',
};

/** 精英词条小标记，排在头顶两侧。 */
function drawEliteMarkers(ctx: Ctx, e: Enemy, x: number, y: number): void {
  const n = e.affixes.length;
  e.affixes.forEach((a, i) => {
    const ang = -Math.PI / 2 + (i - (n - 1) / 2) * 0.55;
    const mx = x + Math.cos(ang) * (e.radius + 10);
    const my = y + Math.sin(ang) * (e.radius + 10);
    ctx.fillStyle = AFFIX_MARKER[a] ?? '#191919';
    ctx.beginPath();
    ctx.arc(mx, my, 2.7, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawOneEnemy(ctx: Ctx, engine: Engine, e: Enemy, x: number, y: number, base: DOMMatrix): void {
  // 生成弹入：ease-out cubic 0.25→1（直接作为绘制 scale，免 save/scale）
  const pk = clamp(e.spawnT / BALANCE.juice.spawnPopTime, 0, 1);
  const pop = 0.25 + 0.75 * (1 - Math.pow(1 - pk, 3));
  if (e.kind === 'chaser') {
    drawSprite(ctx, chaserSprite(e.flash > 0), x, y, pop);
  } else if (e.kind === 'spitter') {
    drawSprite(ctx, spitterSprite(e.flash > 0), x, y, pop);
  } else if (e.kind === 'elite') {
    drawSprite(ctx, eliteSprite(e.flash > 0), x, y, e.radius / BALANCE.enemies.elite.radius * pop);
  } else if (e.kind === 'boss1' || e.kind === 'boss2') {
    const bdef = e.kind === 'boss1' ? BALANCE.enemies.boss1 : BALANCE.enemies.boss2;
    drawSprite(ctx, bossBodySprite(e.kind, e.flash > 0), x, y, e.radius / bdef.radius * pop);
    drawBossTicks(ctx, e, x, y);
  } else if (e.kind === 'charger') {
    const look = chargerLook(e);
    drawSpriteRotT(
      ctx, chargerSprite(look.color, look.key),
      x, y, chargerAngle(e, engine, x, y), pop, base,
    );
  } else {
    const blink = e.state === 'fuse' && Math.sin(e.stateT * 40) > 0;
    if (e.state === 'fuse') drawFuseRing(ctx, e, x, y);
    drawSprite(ctx, bomberSprite(blink), x, y, pop);
  }
  if (e.kind === 'elite') drawEliteMarkers(ctx, e, x, y);
  if (pk < 1) {
    // 生成扩散环
    ctx.strokeStyle = PALETTE.terracotta;
    ctx.globalAlpha = (1 - pk) * 0.55;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, e.radius + 3 + (1 - pk) * 15, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
}

function drawBossTicks(ctx: Ctx, e: Enemy, x: number, y: number): void {
  ctx.strokeStyle = PALETTE.terracotta;
  ctx.lineWidth = 2;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + e.stateT;
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(a) * (e.radius + 2), y + Math.sin(a) * (e.radius + 2));
    ctx.lineTo(x + Math.cos(a) * (e.radius + 7), y + Math.sin(a) * (e.radius + 7));
    ctx.stroke();
  }
}

function drawFuseRing(ctx: Ctx, e: Enemy, x: number, y: number): void {
  const c = BALANCE.enemies.bomber;
  const pulse = 0.25 + 0.25 * Math.abs(Math.sin(e.stateT * 24));
  ctx.strokeStyle = PALETTE.warn;
  ctx.globalAlpha = pulse;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(x, y, c.explodeRadius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

export function drawEnemies(ctx: Ctx, engine: Engine, alpha: number, b: Bounds, base: DOMMatrix): void {
  for (const e of engine.enemyPool.all()) {
    if (!e.active) continue;
    const x = lerp(e.prevX, e.x, alpha);
    const y = lerp(e.prevY, e.y, alpha);
    if (!inView(x, y, b)) continue;
    drawOneEnemy(ctx, engine, e, x, y, base);
  }
}

export function drawBolts(ctx: Ctx, engine: Engine, alpha: number, b: Bounds, base: DOMMatrix): void {
  const staff = staffBoltSprite();
  const arrow = arrowBoltSprite();
  for (const bl of engine.boltPool.all()) {
    if (!bl.active) continue;
    const x = lerp(bl.prevX, bl.x, alpha);
    const y = lerp(bl.prevY, bl.y, alpha);
    if (!inView(x, y, b)) continue;
    if (bl.kind === 'staff') {
      drawSprite(ctx, staff, x, y, bl.size / BALANCE.weapons.staff.size);
    } else {
      drawSpriteRotT(ctx, arrow, x, y, Math.atan2(bl.vy, bl.vx),
        bl.size / BALANCE.weapons.arrow.size, base);
    }
  }
}

export function drawFoeBolts(ctx: Ctx, engine: Engine, alpha: number, b: Bounds): void {
  const sp = foeBoltSprite();
  for (const bl of engine.foeBoltPool.all()) {
    if (!bl.active) continue;
    const x = lerp(bl.prevX, bl.x, alpha);
    const y = lerp(bl.prevY, bl.y, alpha);
    if (!inView(x, y, b)) continue;
    drawSprite(ctx, sp, x, y, bl.radius / BALANCE.enemies.spitter.boltRadius);
  }
}

export function drawSwipes(ctx: Ctx, engine: Engine, b: Bounds): void {
  for (const s of engine.swipePool.all()) {
    if (!s.active) continue;
    if (!inView(s.x, s.y, b)) continue;
    const k = s.life / s.maxLife;
    ctx.globalAlpha = 0.25 + 0.35 * Math.sin(k * Math.PI);
    ctx.fillStyle = PALETTE.ink;
    ctx.beginPath();
    ctx.moveTo(s.x, s.y);
    ctx.arc(s.x, s.y, s.range, s.dir - s.arc / 2, s.dir + s.arc / 2);
    ctx.closePath();
    ctx.fill();
    // 外缘赤陶描边
    ctx.strokeStyle = PALETTE.terracotta;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.range, s.dir - s.arc / 2, s.dir + s.arc / 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }
}

export function drawBoomerangs(ctx: Ctx, engine: Engine, alpha: number, base: DOMMatrix): void {
  for (const bm of engine.weaponSet.boomerang.list) {
    drawSpriteRotT(
      ctx, BOOM_SPRITE,
      lerp(bm.prevX, bm.x, alpha), lerp(bm.prevY, bm.y, alpha),
      bm.spin, 1, base,
    );
  }
}

/** 回旋镖外形（朝 +x 烘焙的描边 V 形）。 */
const BOOM_SPRITE = sprite('boomerang', 8, (x) => {
  x.strokeStyle = PALETTE.terracotta;
  x.lineWidth = 3;
  x.lineCap = 'round';
  x.beginPath();
  x.moveTo(-7, 3);
  x.lineTo(0, -6);
  x.lineTo(7, 3);
  x.stroke();
});

export function drawDamageNumbers(ctx: Ctx, engine: Engine, b: Bounds): void {
  for (const d of engine.damageNumbers.list) {
    if (d.life <= 0) continue;
    if (!inView(d.x, d.y, b)) continue;
    const k = d.life / d.maxLife;
    ctx.globalAlpha = Math.min(1, k * 1.6);
    ctx.fillStyle = d.heal ? PALETTE.heal : d.crit ? PALETTE.warn : PALETTE.ink;
    ctx.font = d.crit
      ? 'bold 18px system-ui, sans-serif'
      : 'bold 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(d.text, d.x, d.y);
  }
  ctx.globalAlpha = 1;
}

export function drawPlayerBody(ctx: Ctx, engine: Engine, x: number, y: number): void {
  const p = engine.player;
  if (p.iframes > 0 && Math.floor(p.iframes * 16) % 2 === 0) return;
  drawSprite(ctx, playerSprite(), x, y);
  // 朝向小楔（走位方向）
  const a = Math.atan2(engine.faceY, engine.faceX);
  ctx.fillStyle = PALETTE.terracotta;
  ctx.beginPath();
  ctx.moveTo(
    x + Math.cos(a) * (BALANCE.player.radius + 8),
    y + Math.sin(a) * (BALANCE.player.radius + 8),
  );
  ctx.lineTo(
    x + Math.cos(a - 0.5) * (BALANCE.player.radius + 2),
    y + Math.sin(a - 0.5) * (BALANCE.player.radius + 2),
  );
  ctx.lineTo(
    x + Math.cos(a + 0.5) * (BALANCE.player.radius + 2),
    y + Math.sin(a + 0.5) * (BALANCE.player.radius + 2),
  );
  ctx.closePath();
  ctx.fill();
  if (p.stats.shield) {
    ctx.strokeStyle = PALETTE.shield;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, y, BALANCE.player.radius + 6, 0, Math.PI * 2);
    ctx.stroke();
  }
}
